import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Shield, Eye, Download, Trash2 } from "lucide-react";


interface PrivacySettingsProps {
  onBack: () => void;
  onNavigateToChat: () => void;
  onNavigateToMyBoyfriends: () => void;
  onNavigateToProfile: () => void;
  onNavigateToSettings: () => void;
  onCreateNew: () => void;
  user?: {
    email?: string;
    name?: string;
  };
}

export const PrivacySettings = ({ 
  onBack, 
  onNavigateToChat, 
  onNavigateToMyBoyfriends, 
  onNavigateToProfile, 
  onNavigateToSettings,
  onCreateNew,
  user 
}: PrivacySettingsProps) => {
  const [privacySettings, setPrivacySettings] = useState({
    showOnlineStatus: true,
    allowDataCollection: false,
    shareUsageStats: false,
    personalizedAds: false,
    messageHistory: true,
    profileVisibility: true,
  });

  const handleToggle = (setting: keyof typeof privacySettings) => {
    setPrivacySettings(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
  };

  const handleExportData = () => {
    // Implement data export functionality
    console.log("Exporting user data...");
  };

  const handleDeleteAccount = () => {
    // Implement account deletion functionality
    console.log("Deleting account...");
  };

  return (
    <div className="bg-gradient-dreamy min-h-screen pb-16">
      {/* Main Content */}
      <div className="p-4 overflow-y-auto">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6 pb-6 border-b border-primary/20">
            <Button variant="ghost" size="icon" onClick={onNavigateToProfile}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-bold bg-gradient-romantic bg-clip-text text-transparent">
              Privacy Settings
            </h1>
          </div>


          {/* Data Collection */}
          <Card className="p-6 mb-6 border-primary/20">
            <div className="flex items-center gap-2 mb-4">
              <Eye className="h-5 w-5" />
              <h3 className="font-semibold">Data Collection</h3>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-lg bg-card/50">
                <div>
                  <h4 className="font-medium">Usage Analytics</h4>
                  <p className="text-sm text-muted-foreground">
                    Help improve our service by sharing usage data
                  </p>
                </div>
                <Switch 
                  checked={privacySettings.allowDataCollection}
                  onCheckedChange={() => handleToggle('allowDataCollection')}
                />
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg bg-card/50">
                <div>
                  <h4 className="font-medium">Share Usage Statistics</h4>
                  <p className="text-sm text-muted-foreground">
                    Share anonymous usage statistics
                  </p>
                </div>
                <Switch 
                  checked={privacySettings.shareUsageStats}
                  onCheckedChange={() => handleToggle('shareUsageStats')}
                />
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg bg-card/50">
                <div>
                  <h4 className="font-medium">Personalized Ads</h4>
                  <p className="text-sm text-muted-foreground">
                    Show ads based on your interests
                  </p>
                </div>
                <Switch 
                  checked={privacySettings.personalizedAds}
                  onCheckedChange={() => handleToggle('personalizedAds')}
                />
              </div>
            </div>
          </Card>

          {/* Data Management */}
          <Card className="p-6 mb-6 border-primary/20">
            <div className="flex items-center gap-2 mb-4">
              <Download className="h-5 w-5" />
              <h3 className="font-semibold">Data Management</h3>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-lg bg-card/50">
                <div>
                  <h4 className="font-medium">Export My Data</h4>
                  <p className="text-sm text-muted-foreground">
                    Download all your data including conversations
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={handleExportData}>
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </div>
            </div>
          </Card>

          {/* Account Deletion */}
          <Card className="p-6 border-red-200 border-2">
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-lg bg-red-50 dark:bg-red-950/20">
                <div>
                  <h4 className="font-medium text-red-600">Delete Account</h4>
                  <p className="text-sm text-red-500">
                    Permanently delete your account and all data
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={handleDeleteAccount} className="text-red-600 border-red-300 hover:bg-red-50">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};